import {
    type FC,
    useEffect,
    useRef,
    useState,
    useContext,
    useCallback,
    type ChangeEvent,
    useTransition,
} from 'react';
import classnames from 'classnames';
import { useRefetchableFragment, graphql } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { FormattedMessage } from 'dibs-react-intl';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import MagnifyingGlass from 'dibs-icons/exports/legacy/MagnifyingGlass.ts';
import Close from 'dibs-icons/exports/legacy/Close.ts';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS } from './GetHelpZendeskConstants.ts';
import { separateSearchQueryFromString } from './getHelpZendeskHelpers.ts';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';

import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';

import { type GetHelpZendeskArticlesSearch_viewer$key } from './__generated__/GetHelpZendeskArticlesSearch_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskArticlesSearchSharedStyles.scss';

const MIN_SEARCH_LENGTH = 3;

const messages = defineMessages({
    placeholder: {
        id: 'getHelp.GetHelpZendeskArticlesSearch.placeholder',
        defaultMessage: 'Search 1stDibs Help',
    },
});

const GetHelpZendeskArticlesSearch: FC<{ viewer: GetHelpZendeskArticlesSearch_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const intl = useIntl();
    const {
        state: { searchQuery, isSeller },
        dispatch,
    } = useContext(GetHelpZendeskContext);
    const [query, setQuery] = useState(searchQuery);
    const [showResults, setShowResults] = useState(false);
    const [fetching, startTransition] = useTransition();
    const searchRef = useRef<HTMLDivElement | null>(null);

    const [viewer, refetch] = useRefetchableFragment(
        graphql`
            fragment GetHelpZendeskArticlesSearch_viewer on Viewer
            @argumentDefinitions(query: { type: "String", defaultValue: "" })
            @refetchable(queryName: "GetHelpZendeskArticlesSearchRefetchQuery") {
                zendeskArticles(isSeller: $isSeller, sellerId: $sellerId) {
                    articlesSearch: articles(query: $query) {
                        serviceId
                        title
                    }
                    promotedArticles(isCheckout: $isCheckout) {
                        serviceId
                        title
                    }
                }
            }
        `,
        viewerRef
    );

    useEffect(() => {
        setQuery(searchQuery);
    }, [searchQuery]);

    const fetchData = useCallback(
        (value: string) => {
            startTransition(() => {
                refetch({ query: value });
            });
        },
        [refetch, startTransition]
    );

    const [debouncedFetchData] = useDebouncedCallback(fetchData, 200);

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        setQuery(value);

        if (value.length >= MIN_SEARCH_LENGTH || !value) {
            debouncedFetchData(value);
        }
    };

    const onSearch = (): void => {
        dispatch({ type: ACTIONS.SET_SEARCH_RESULTS_STEP, searchQuery: query });
        trackGetHelpZendeskEvent({
            stepInteractionName: 'search query entered',
            isSeller,
        });
    };

    const zendeskArticles = viewer.zendeskArticles;
    const suggestions = (zendeskArticles?.articlesSearch || []).slice(0, 5);
    const promotedArticles = zendeskArticles?.promotedArticles || [];

    const searchIcon = (
        <div
            className={classnames(
                styles.searchButton,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                { [dibsCss.bgNoir]: !!query && !isSeller },
                { [dibsCss.bgDealerprimary]: !!query && isSeller }
            )}
        >
            <MagnifyingGlass
                className={classnames(dibsCss.h20px, dibsCss.w20px, {
                    [dibsCss.fillWhite]: !!query,
                })}
            />
        </div>
    );

    return (
        <div ref={searchRef}>
            <Input
                size="medium"
                dataTn="get-help-zendesk-articles-search-input"
                placeholder={intl.formatMessage(messages.placeholder)}
                value={query}
                onChange={onChange}
                rightDecoratorClass={styles.rightDecorator}
                onBlur={e => {
                    if (searchRef?.current && !searchRef.current.contains(e.relatedTarget)) {
                        setShowResults(false);
                    }
                }}
                onFocus={() => {
                    setShowResults(true);
                }}
                rightDecorator={
                    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
                        {query && (
                            <>
                                <Link
                                    onClick={e => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setQuery('');
                                        setShowResults(false);
                                        fetchData('');
                                    }}
                                >
                                    <span className={dibsCss.flex}>
                                        <Close
                                            className={classnames(
                                                dibsCss.w14px,
                                                dibsCss.h14px,
                                                dibsCss.mrXsmall
                                            )}
                                        />
                                    </span>
                                </Link>
                                <Link onClick={onSearch}>{searchIcon}</Link>
                            </>
                        )}
                        {!query && searchIcon}
                    </div>
                }
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        onSearch();
                    }
                }}
            />
            {showResults && ((!!query && (suggestions.length || fetching)) || !query) && (
                <div className={dibsCss.relative}>
                    <div
                        className={classnames(
                            dibsCss.absolute,
                            dibsCss.top0,
                            dibsCss.pyXsmall,
                            dibsCss.border,
                            dibsCss.borderSolid,
                            dibsCss.borderNoir,
                            dibsCss.borderT0,
                            dibsCss.wFull,
                            dibsCss.bgWhite
                        )}
                    >
                        {fetching && (
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.justifyCenter,
                                    dibsCss.wFull
                                )}
                            >
                                <Spinner size="tiny" />
                            </div>
                        )}
                        {!fetching &&
                            suggestions.map(suggestion => (
                                <Link
                                    key={suggestion?.serviceId || ''}
                                    onClick={() => {
                                        setQuery('');
                                        setShowResults(false);
                                        dispatch({
                                            type: ACTIONS.OPEN_ARTICLE,
                                            articleId: (suggestion?.serviceId || '').split('-')[0],
                                        });
                                        trackGetHelpZendeskEvent({
                                            stepInteractionName: 'search result clicked',
                                            trigger: 'search dropdown',
                                            isSeller,
                                        });
                                    }}
                                    className={dibsCss.block}
                                    underline="none"
                                >
                                    <div
                                        className={classnames(
                                            styles.label,
                                            dibsCss.pyXxsmall,
                                            dibsCss.pxSmall,
                                            dibsCss.sassyFontBodyMedium,
                                            dibsCss.textLeft
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: separateSearchQueryFromString(
                                                suggestion?.title || '',
                                                query
                                            ),
                                        }}
                                    />
                                </Link>
                            ))}
                        {!fetching && !query && (
                            <>
                                <div
                                    className={classnames(
                                        dibsCss.sassyFontSubHeaderMedium,
                                        dibsCss.textSatan,
                                        dibsCss.mlSmall,
                                        dibsCss.mtXxsmall
                                    )}
                                >
                                    <FormattedMessage
                                        id="getHelp.GetHelpZendeskArticlesSearch.popularSearches"
                                        defaultMessage="Popular searches"
                                    />
                                </div>
                                {promotedArticles.map(promotedArticle => (
                                    <Link
                                        underline="none"
                                        className={classnames(dibsCss.block, dibsCss.textLeft)}
                                        key={promotedArticle?.serviceId || ''}
                                        onClick={() => {
                                            setShowResults(false);
                                            dispatch({
                                                type: ACTIONS.OPEN_ARTICLE,
                                                articleId: (promotedArticle?.serviceId || '').split(
                                                    '-'
                                                )[0],
                                            });
                                        }}
                                    >
                                        <div
                                            className={classnames(
                                                styles.label,
                                                dibsCss.pyXxsmall,
                                                dibsCss.pxSmall,
                                                dibsCss.sassyFontBodyMedium,
                                                dibsCss.textLeft
                                            )}
                                        >
                                            {promotedArticle?.title}
                                        </div>
                                    </Link>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export { GetHelpZendeskArticlesSearch };
